<div>
  <h4 class="header-title">{{ tab_name }} : Signal History</h4>
  <p class="header-subtitle">
    The graph below displays the history of the stock performance of {{ tab_name }} against the innovation score and the resulting buy / sell signal over the last 10 years.
  </p>

  <div class="d-flex justify-content-center my-4" *ngIf="showSpinner">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <div class="alert alert-danger" *ngIf="loadingFailed">
    Failed to load innovation history. Please try again or contact
    <a href="mailto:info@quant-ip.com">info&#64;quant-ip.com</a>.
  </div>

  <div class="alert alert-info d-flex align-items-center" *ngIf="isUnsupportedCompanyType">
    <i class="bi bi-info-circle me-2"></i>
    Signal History is only available for public companies.
  </div>

  <div class="empty-state" *ngIf="showDataMissing && !showSpinner">
    <i class="bi bi-search empty-state-icon"></i>
    <h5>No Signal History Available</h5>
    <p>There haven't been any inventions published in the last 10 years to display signal history.</p>
  </div>

  <ng-container *ngIf="showChart && !showSpinner">
    <div class="alert alert-warning d-flex align-items-center" *ngIf="!hasMcap" data-html2canvas-ignore="true">
      <i class="bi bi-exclamation-triangle me-2"></i>
      Stock price data is not available for this company. The BUY / HOLD / SELL signal is still shown.
    </div>

    <div #exportTarget class="chart-card">
      <div class="card-header-row">
        <h5 class="chart-title">{{ chartCardTitle }}</h5>
        <div class="chart-toolbar" data-html2canvas-ignore="true" *ngIf="!printMode">
          <button type="button" class="tool-btn" (click)="resetZoom()" title="Reset zoom">
            <i class="bi bi-arrow-counterclockwise"></i>
          </button>
          <button type="button" class="tool-btn" (click)="downloadPng()" title="Download PNG">
            <i class="bi bi-download"></i>
          </button>
        </div>
      </div>

      <div class="chip-row" *ngIf="!printMode">
        <div class="metric-chips">
          <button type="button" class="metric-chip" [class.active]="timeRange === '10y'"
            (click)="setTimeRange('10y')">
            <span class="chip-dot" *ngIf="timeRange === '10y'" style="background:#475569"></span>
            10Y
          </button>
          <button type="button" class="metric-chip" [class.active]="timeRange === '3y'"
            (click)="setTimeRange('3y')">
            <span class="chip-dot" *ngIf="timeRange === '3y'" style="background:#475569"></span>
            3Y
          </button>
          <button type="button" class="metric-chip" [class.active]="timeRange === '1y'"
            (click)="setTimeRange('1y')">
            <span class="chip-dot" *ngIf="timeRange === '1y'" style="background:#475569"></span>
            1Y
          </button>
        </div>

        <div class="metric-chips">
          <button type="button" class="metric-chip" [class.active]="secondaryAxis === 'innovation_score'"
            (click)="toggleSecondaryAxis('innovation_score')"
            matTooltip="Overlay Innovation Score (0–100) on the right axis"
            matTooltipPosition="above">
            <span class="chip-dot" *ngIf="secondaryAxis === 'innovation_score'"
              [style.background]="SECONDARY_CFG.innovation_score.color"></span>
            <i class="bi bi-lightbulb"></i> Score
          </button>
          <button type="button" class="metric-chip" [class.active]="secondaryAxis === 'invention_count_120m'"
            (click)="toggleSecondaryAxis('invention_count_120m')"
            matTooltip="Number of inventions published in the last 120 months."
            matTooltipPosition="above">
            <span class="chip-dot" *ngIf="secondaryAxis === 'invention_count_120m'"
              [style.background]="SECONDARY_CFG.invention_count_120m.color"></span>
            <i class="bi bi-bar-chart"></i> Size
          </button>
          <button type="button" class="metric-chip" [class.active]="secondaryAxis === 'relative_growth_12m'"
            (click)="toggleSecondaryAxis('relative_growth_12m')"
            matTooltip="% Year-over-year ratio. Number of inventions filed in the last 12 months divided by the prior 12 months."
            matTooltipPosition="above">
            <span class="chip-dot" *ngIf="secondaryAxis === 'relative_growth_12m'"
              [style.background]="SECONDARY_CFG.relative_growth_12m.color"></span>
            <i class="bi bi-graph-up-arrow"></i> Growth
          </button>
          <button type="button" class="metric-chip" [class.active]="secondaryAxis === 'quality_score_12m'"
            (click)="toggleSecondaryAxis('quality_score_12m')"
            matTooltip="Average quality score of inventions published in the last 12 months."
            matTooltipPosition="above">
            <span class="chip-dot" *ngIf="secondaryAxis === 'quality_score_12m'"
              [style.background]="SECONDARY_CFG.quality_score_12m.color"></span>
            <i class="bi bi-award"></i> Quality
          </button>
        </div>
      </div>

      <p class="zoom-hint" data-html2canvas-ignore="true" *ngIf="!printMode">Drag to zoom • Shift + drag to pan  • Double-click to reset</p>

      <div class="chart-container">
        <canvas baseChart
          [type]="'bar'"
          [data]="chartjsData"
          [options]="chartjsOptions"
          [plugins]="chartPlugins"
          (dblclick)="resetZoom()">
        </canvas>
      </div>

      <div class="chart-legend">
        <span class="legend-item">
          <span class="legend-line"></span> {{ primaryLabel }}
        </span>
        <span class="legend-item">
          <span class="legend-marker legend-marker--up" [style.border-bottom-color]="SIGNAL.BUY"></span> Buy
        </span>
        <span class="legend-item">
          <span class="legend-marker legend-marker--circle" [style.background]="SIGNAL.HOLD"></span> Hold
        </span>
        <span class="legend-item">
          <span class="legend-marker legend-marker--down" [style.border-top-color]="SIGNAL.SELL"></span> Sell
        </span>
        <ng-container *ngIf="secondaryAxis">
          <span class="legend-item" *ngIf="SECONDARY_CFG[secondaryAxis].type === 'bar'">
            <span class="legend-bar" [style.background]="SECONDARY_CFG[secondaryAxis].color"></span>
            {{ SECONDARY_CFG[secondaryAxis].label }}
          </span>
          <span class="legend-item" *ngIf="SECONDARY_CFG[secondaryAxis].type === 'scatter'">
            <span class="legend-line" [style.background]="SECONDARY_CFG[secondaryAxis].color"></span>
            {{ SECONDARY_CFG[secondaryAxis].label }}
          </span>
        </ng-container>
        <span class="chart-footnote">
          * End of month stock price determined based on the latest trading day of the month.
        </span>
      </div>
    </div>
  </ng-container>

    <div style="font-size: 10px;margin-top:20px; margin-bottom:10px;">
        <span style="font-size: 10px;margin-top:20px; margin-bottom:10px;">
        Important Notice: 
This content is provided for general informational purposes only. It reflects a quantitative assessment of a company’s innovation activity based primarily on patent-related data and does not constitute investment advice, investment brokerage, portfolio management, a personal recommendation, or an offer or solicitation to buy, sell, or hold any security. The rating shown on this page, including any “BUY”, “HOLD”, or “SELL” label, is generated by a model using patent and related innovation signals only. It does not take into account any investor’s individual investment objectives, financial situation, tax position, liquidity needs, risk tolerance, existing holdings, or suitability requirements. The model does not incorporate all factors relevant to an investment decision, including valuation, financial performance, balance sheet strength, macroeconomic conditions, governance, litigation, competitive dynamics, or market sentiment. This content must not be used as the sole basis for any investment decision. 
     </span>
      </div>
</div>
